import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';

import logger from 'utils/logger';

import useReactionHistory from './useReactionHistory';
import useReactionListeners from './useReactionListeners';

export interface RaisedHandInfo {
  membershipEventId: string;
  raiseHandEventId: string;
}

export type RaisedHandsMap = Map<string, RaisedHandInfo>;

export interface ReactionInfo {
  membershipEventId: string;
  reactionEventId: string;
  reactionKey: string;
}

export type ReactionsMap = Map<string, ReactionInfo>;

export interface ReactionsContextType {
  raisedHands: RaisedHandsMap;
  reactions: ReactionsMap;
  removeReaction: (userId: string) => void;
}

export const ReactionsContext = createContext<ReactionsContextType | null>(null);

export const ReactionsContextProvider = ({ children, value }: PropsWithChildren<{ value?: ReactionsContextType }>) => {
  const [raisedHands, setRaisedHands] = useState<RaisedHandsMap>(new Map());
  const [reactions, setReactions] = useState<ReactionsMap>(new Map());

  useReactionHistory();

  useEffect(() => {
    logger.info('Raised hands state:', raisedHands);
  }, [raisedHands]);

  useEffect(() => {
    logger.info('Reactions state:', reactions);
  }, [reactions]);

  const addReaction = useCallback(
    (userId: string, info: ReactionInfo) =>
      setReactions((prev) => {
        const newMap = new Map(prev);
        newMap.set(userId, info);
        return newMap;
      }),
    []
  );

  const removeReaction = useCallback(
    (userId: string) =>
      setReactions((prev) => {
        const newMap = new Map(prev);
        newMap.delete(userId);
        return newMap;
      }),
    []
  );

  const addRaisedHand = useCallback(
    (userId: string, info: RaisedHandInfo) =>
      setRaisedHands((prev) => {
        const newMap = new Map(prev);
        newMap.set(userId, info);
        return newMap;
      }),
    []
  );

  const removeRaisedHand = useCallback(
    (userId: string) =>
      setRaisedHands((prev) => {
        const newMap = new Map(prev);
        newMap.delete(userId);
        return newMap;
      }),
    []
  );

  useReactionListeners({ raisedHands, addRaisedHand, removeRaisedHand, addReaction });

  return (
    <ReactionsContext.Provider value={{ raisedHands, reactions, removeReaction, ...value }}>
      {children}
    </ReactionsContext.Provider>
  );
};
